"use client";

import { useRef, useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { META_ANALYSIS_CALCULATOR_STRINGS } from "@/components/tools/meta-analysis-calculator";
import { ForestPlot } from "@/components/tools/stats-research/forest-plot";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  apaPClause,
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";
import {
  type MetaAnalysisResult,
  type PooledEstimate,
  poolStudies,
  type StudyEffect,
} from "@/lib/tool-kit/stats-research/meta-analysis";
import { zFor } from "@/lib/tool-kit/stats-research/proportions";

/*
 * The result panel of the meta-analysis calculator: pooled estimates, heterogeneity,
 * the study table, the forest plot with its downloads, and the working.
 */

type Strings = typeof META_ANALYSIS_CALCULATOR_STRINGS;
type Model = "random" | "fixed";
type Study = StudyEffect & { label: string };

const FEW_STUDIES = 10;
const Z95 = zFor(0.95);

function Interval({ text }: { text: string }) {
  return (
    <span className="mt-1 block font-normal text-base text-muted-foreground">
      {text}
    </span>
  );
}

function ModelTable({
  result,
  show,
  strings,
  fmt,
}: {
  result: MetaAnalysisResult;
  show: (value: number) => string;
  strings: Strings;
  fmt: Formatter;
}) {
  const rows: [string, PooledEstimate][] = [
    [strings.modelFixed, result.fixed],
    [strings.modelRandom, result.random],
  ];
  const head = [
    strings.columnModel,
    strings.columnEstimate,
    strings.columnInterval,
    strings.columnZ,
    strings.columnP,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, pooled]) => (
            <tr key={label}>
              <th className={`${TD} font-medium`} scope="row">
                {label}
              </th>
              <td className={`${TD} font-mono`}>{show(pooled.estimate)}</td>
              <td className={`${TD} font-mono`}>
                [{show(pooled.lower)}, {show(pooled.upper)}]
              </td>
              <td className={`${TD} font-mono`}>{fixed(fmt, pooled.z, 2)}</td>
              <td className={`${TD} font-mono`}>{formatP(fmt, pooled.p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StudyTable({
  studies,
  result,
  show,
  strings,
  fmt,
}: {
  studies: Study[];
  result: MetaAnalysisResult;
  show: (value: number) => string;
  strings: Strings;
  fmt: Formatter;
}) {
  const head = [
    strings.columnStudy,
    strings.columnEstimate,
    strings.columnInterval,
    strings.columnWeightFixed,
    strings.columnWeightRandom,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {studies.map((study, i) => {
            const half = Z95 * Math.sqrt(study.vi);
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: names may repeat
              <tr key={i}>
                <th className={`${TD} font-medium`} scope="row">
                  {study.corrected ? `${study.label} †` : study.label}
                </th>
                <td className={`${TD} font-mono`}>{show(study.yi)}</td>
                <td className={`${TD} font-mono`}>
                  [{show(study.yi - half)}, {show(study.yi + half)}]
                </td>
                <td className={`${TD} font-mono`}>
                  {fixed(fmt, result.fixedWeights[i], 1)}%
                </td>
                <td className={`${TD} font-mono`}>
                  {fixed(fmt, result.randomWeights[i], 1)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Working({
  result,
  ratio,
  strings,
  fmt,
}: {
  result: MetaAnalysisResult;
  ratio: boolean;
  strings: Strings;
  fmt: Formatter;
}) {
  const four = (value: number) => fixed(fmt, value, 4);
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
      {ratio ? (
        <p className="text-muted-foreground text-sm">{strings.workingLog}</p>
      ) : null}
      <p className={line}>
        {t(strings.workingFixed, {
          sumW: four(result.sumWeights),
          estimate: four(result.fixed.estimate),
          se: four(result.fixed.se),
        })}
      </p>
      <p className={line}>
        {t(strings.workingTau, {
          c: four(result.c),
          q: four(result.q),
          df: fmt(result.df),
          tau2: four(result.tau2),
        })}
      </p>
      <p className={line}>
        {t(strings.workingRandom, {
          estimate: four(result.random.estimate),
          se: four(result.random.se),
        })}
      </p>
    </div>
  );
}

function Heterogeneity({
  result,
  ratio,
  show,
  strings,
  fmt,
}: {
  result: MetaAnalysisResult;
  ratio: boolean;
  show: (value: number) => string;
  strings: Strings;
  fmt: Formatter;
}) {
  const sentences = [
    t(strings.heterogeneityText, {
      q: fixed(fmt, result.q, 2),
      df: fmt(result.df),
      p: formatP(fmt, result.qP),
      i2: fixed(fmt, result.i2, 1),
      tau2: fixed(fmt, result.tau2, 4),
    }),
  ];
  if (ratio) {
    sentences.push(strings.tauLogScale);
  }
  sentences.push(
    result.prediction
      ? t(strings.predictionText, {
          lower: show(result.prediction.lower),
          upper: show(result.prediction.upper),
        })
      : strings.predictionNeedsThree
  );
  return (
    <>
      <p className="text-foreground text-sm leading-relaxed">
        {sentences.join(" ")}
      </p>
      {result.k < FEW_STUDIES ? <Notice>{strings.fewStudies}</Notice> : null}
    </>
  );
}

export function MetaSummary({
  studies,
  ratio,
  axisLabel,
  strings,
  fmt,
}: {
  studies: Study[];
  ratio: boolean;
  axisLabel: string;
  strings: Strings;
  fmt: Formatter;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [model, setModel] = useState<Model>("random");
  const result = poolStudies(studies);
  if (!result) {
    return <Notice>{strings.needTwo}</Notice>;
  }
  const show = (value: number) =>
    fixed(fmt, ratio ? Math.exp(value) : value, 3);
  const two = (value: number) => fixed(fmt, ratio ? Math.exp(value) : value, 2);
  const range = (pooled: { lower: number; upper: number }) =>
    `[${show(pooled.lower)}, ${show(pooled.upper)}]`;
  const report = t(strings.report, {
    value: two(result.random.estimate),
    lower: two(result.random.lower),
    upper: two(result.random.upper),
    z: fixed(fmt, result.random.z, 2),
    p: apaPClause(fmt, result.random.p),
    df: fmt(result.df),
    q: fixed(fmt, result.q, 2),
    qp: apaPClause(fmt, result.qP),
    i2: fixed(fmt, result.i2, 1),
    tau2: fixed(fmt, result.tau2, 3),
  });
  const weights =
    model === "random" ? result.randomWeights : result.fixedWeights;

  async function savePng() {
    if (svgRef.current) {
      try {
        await downloadPng(`${strings.fileName}.png`, svgRef.current);
      } catch {
        // A browser that cannot rasterise SVG still offers the SVG download.
      }
    }
  }

  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.randomCard}
          value={
            <>
              {show(result.random.estimate)}
              <Interval text={range(result.random)} />
            </>
          }
        />
        <StatCard
          label={strings.fixedCard}
          value={
            <>
              {show(result.fixed.estimate)}
              <Interval text={range(result.fixed)} />
            </>
          }
        />
        <StatCard
          label={strings.i2Card}
          value={`${fixed(fmt, result.i2, 1)}%`}
        />
        <StatCard label={strings.tau2Card} value={fixed(fmt, result.tau2, 4)} />
        <StatCard
          label={t(strings.qCard, { df: fmt(result.df) })}
          value={
            <>
              {fixed(fmt, result.q, 2)}
              <Interval text={`p = ${formatP(fmt, result.qP)}`} />
            </>
          }
        />
        <StatCard
          label={strings.predictionCard}
          value={result.prediction ? range(result.prediction) : "—"}
        />
      </StatGrid>
      <ModelTable fmt={fmt} result={result} show={show} strings={strings} />
      <Heterogeneity
        fmt={fmt}
        ratio={ratio}
        result={result}
        show={show}
        strings={strings}
      />
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <p className="font-mono text-muted-foreground text-sm">{report}</p>
      </div>
      <h3 className={WIDGET_H3}>{strings.studiesHeading}</h3>
      <StudyTable
        fmt={fmt}
        result={result}
        show={show}
        strings={strings}
        studies={studies}
      />
      {studies.some((study) => study.corrected) ? (
        <p className="text-muted-foreground text-xs">{strings.correctedNote}</p>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <PillGroup
        label={strings.plotWeights}
        name="ma-plot-model"
        onChange={setModel}
        options={[
          { value: "random", label: strings.plotRandom },
          { value: "fixed", label: strings.plotFixed },
        ]}
        value={model}
      />
      <div className="overflow-x-auto rounded-lg border border-border bg-white p-2">
        <ForestPlot
          axisLabel={axisLabel}
          fmt={fmt}
          headings={{
            study: strings.headingStudy,
            estimate: strings.headingEstimate,
            weight: strings.headingWeight,
          }}
          pooled={[
            {
              label: strings.pooledFixed,
              ...result.fixed,
              color: CHART_COLORS[2],
            },
            {
              label: strings.pooledRandom,
              ...result.random,
              color: CHART_COLORS[3],
            },
          ]}
          ratio={ratio}
          studies={studies.map((study, i) => {
            const half = Z95 * Math.sqrt(study.vi);
            return {
              label: study.label,
              estimate: study.yi,
              lower: study.yi - half,
              upper: study.yi + half,
              weight: weights[i],
            };
          })}
          svgRef={svgRef}
          title={strings.plotTitle}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            if (svgRef.current) {
              downloadSvg(`${strings.fileName}.svg`, svgRef.current);
            }
          }}
          type="button"
        >
          {strings.downloadSvg}
        </button>
        <button className={BUTTON_SMALL} onClick={savePng} type="button">
          {strings.downloadPng}
        </button>
      </div>
      <Working fmt={fmt} ratio={ratio} result={result} strings={strings} />
    </section>
  );
}
